import { useMemo } from "react";
import { formatMoney } from "@/lib/prefs";

/**
 * LaborPerRecipeChart with generative particle flow.
 *
 * Each bar is a small SVG canvas that carries three layers:
 *   1. A muted champagne channel that shows the maximum possible length.
 *   2. A luminous gold fill whose width animates from 0 to its target on
 *      mount and morphs whenever the underlying data changes. The fill
 *      itself carries a moving gradient (60% infinite) that reads as
 *      shimmering liquid gold.
 *   3. A cluster of drifting particles that flow left to right inside
 *      the fill and disappear when they reach the leading edge. This
 *      is what gives the chart its "generative" living feel without
 *      any JavaScript animation loop or canvas.
 * All animation runs on the compositor thread via `transform` on
 * CSS keyframes, so the effect stays smooth even with a dozen rows.
 */
export default function LaborPerRecipeChart({ recipes, ingredientsById, avgRate }) {
  const data = useMemo(() => {
    return recipes
      .map((r) => {
        const totalPrep = (r.ingredients || []).reduce(
          (s, l) => s + ((ingredientsById[l.ingredient_id]?.prep_time_minutes) || 0),
          0,
        );
        const hours = totalPrep / 60;
        return { name: r.name, cost: hours * avgRate, hours };
      })
      .filter((d) => d.cost > 0)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 8);
  }, [recipes, ingredientsById, avgRate]);

  if (!data.length) {
    return (
      <div className="rounded-2xl glass-card p-6" data-testid="labor-chart-empty">
        <div className="label-eyebrow mb-2">Labor per recipe</div>
        <p className="text-sm text-muted-foreground">
          Add prep time to ingredients and staff to your roster to see labor cost per recipe.
        </p>
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.cost));

  return (
    <div className="rounded-2xl glass-card p-6" data-testid="labor-chart">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="label-eyebrow">Labor per recipe</div>
          <h3 className="font-display text-xl mt-1">Avg rate × prep time</h3>
        </div>
        <div className="text-right">
          <div className="label-eyebrow text-[0.55rem]">Avg rate</div>
          <div className="font-display text-lg tabular">
            {formatMoney(avgRate)}
            <span className="text-xs text-muted-foreground">/h</span>
          </div>
        </div>
      </div>

      {/* Shared SVG defs: one gradient + one glow filter that every bar
          references so the browser only allocates them once. */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="lpr-gold" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"  stopColor="#7a5a1e" stopOpacity="0.85" />
            <stop offset="45%" stopColor="#e8cf9a" stopOpacity="1"    />
            <stop offset="75%" stopColor="#c8a96a" stopOpacity="1"    />
            <stop offset="100%" stopColor="#7a5a1e" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="lpr-shimmer" x1="-100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%"  stopColor="rgba(255,255,255,0)" />
            <stop offset="45%" stopColor="rgba(255,255,255,0.32)" />
            <stop offset="55%" stopColor="rgba(255,255,255,0.32)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            <animate attributeName="x1" from="-100%" to="200%" dur="3.2s" repeatCount="indefinite" />
            <animate attributeName="x2" from="0%"    to="300%" dur="3.2s" repeatCount="indefinite" />
          </linearGradient>
          <filter id="lpr-glow" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
        </defs>
      </svg>

      <div className="space-y-3">
        {data.map((d, idx) => {
          const pct = (d.cost / max) * 100;
          // 3 particles per row, staggered so they never bunch.
          const particleDelays = [0, 1.1, 2.2];
          return (
            <div
              key={d.name}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-sm"
              data-testid={`labor-row-${idx}`}
            >
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="truncate mr-2">{d.name}</div>
                  <div className="tabular text-xs text-muted-foreground shrink-0">
                    {d.hours.toFixed(2)}h
                  </div>
                </div>

                <div className="relative h-2.5 rounded-full overflow-hidden bg-white/[0.04] border border-white/[0.05]">
                  {/* The animated fill. `width` is a CSS transition so it
                      morphs whenever the target changes (data refresh,
                      hover elsewhere, etc.). The gold gradient shimmer
                      inside is a keyframe on background-position, so it
                      runs on the compositor thread. */}
                  <div
                    className="lpr-fill h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      animationDelay: `${idx * 90}ms`,
                    }}
                  >
                    {/* Particle layer: three glowing dots that drift
                        left to right and fade as they hit the leading
                        edge. Sits inside the fill so particles never
                        leak past the animated width. */}
                    {particleDelays.map((delay, pi) => (
                      <span
                        key={pi}
                        className="lpr-particle"
                        style={{
                          animationDelay: `${delay + idx * 0.15}s`,
                          top: `${25 + pi * 25}%`,
                        }}
                      />
                    ))}
                    {/* Leading edge halo: a soft luminous dot at the
                        exact tip of the bar so the eye is drawn there
                        naturally. */}
                    <span className="lpr-tip" />
                  </div>
                </div>
              </div>
              <div className="font-display tabular w-20 text-right">{formatMoney(d.cost)}</div>
            </div>
          );
        })}
      </div>

      {/* Scoped keyframes + fill styling. Kept local so the effect only
          exists where the chart lives. */}
      <style>{`
        .lpr-fill {
          position: relative;
          background:
            linear-gradient(
              90deg,
              rgba(122, 90, 30, 0.75) 0%,
              rgba(232, 207, 154, 1) 40%,
              rgba(200, 169, 106, 1) 70%,
              rgba(122, 90, 30, 0.75) 100%
            );
          background-size: 220% 100%;
          background-position: 0% 50%;
          animation: lpr-flow 6s linear infinite, lpr-grow 900ms cubic-bezier(0.22, 1, 0.36, 1) both;
          box-shadow:
            0 0 12px rgba(232, 207, 154, 0.35),
            inset 0 0 0.5px rgba(255, 255, 255, 0.35);
          overflow: hidden;
          transform: translateZ(0);
          will-change: background-position, width;
        }
        @keyframes lpr-flow {
          0%   { background-position: 0%   50%; }
          100% { background-position: 220% 50%; }
        }
        @keyframes lpr-grow {
          from { transform: translateZ(0) scaleX(0); transform-origin: left center; }
          to   { transform: translateZ(0) scaleX(1); transform-origin: left center; }
        }

        .lpr-particle {
          position: absolute;
          left: 0;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.95), rgba(232, 207, 154, 0.5) 60%, rgba(232, 207, 154, 0) 100%);
          transform: translate3d(-8px, -50%, 0);
          filter: blur(0.3px);
          opacity: 0;
          animation: lpr-particle 3.4s linear infinite;
          will-change: transform, opacity;
        }
        @keyframes lpr-particle {
          0%   { transform: translate3d(-8px, -50%, 0) scale(0.7); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 0.9; }
          100% { transform: translate3d(calc(100% + 6px), -50%, 0) scale(1); opacity: 0; }
        }

        .lpr-tip {
          position: absolute;
          right: 0;
          top: 50%;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          transform: translate(35%, -50%);
          background: radial-gradient(circle, rgba(255, 245, 220, 0.95) 0%, rgba(232, 207, 154, 0.6) 45%, rgba(232, 207, 154, 0) 75%);
          animation: lpr-tip-pulse 2.4s ease-in-out infinite;
          will-change: opacity, transform;
        }
        @keyframes lpr-tip-pulse {
          0%, 100% { opacity: 0.75; transform: translate(35%, -50%) scale(1); }
          50%      { opacity: 1;    transform: translate(35%, -50%) scale(1.35); }
        }

        @media (prefers-reduced-motion: reduce) {
          .lpr-fill { animation: none; }
          .lpr-particle, .lpr-tip { animation: none; opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
